import { z } from 'zod';
import { CadDeclaredMaterialSchema } from './cadImport.js';
import type { OptomechMaterial, OptomechThermoOptic } from './optomech.js';

/**
 * Optomech materials and body bindings — spec
 * docs/superpowers/specs/2026-10-16-optomech-parts-library-design.md §4.1, §4.3.
 *
 * Units here are DATASHEET units (GPa, g/cm³, 10⁻⁶/K, °C), the ones a source
 * quotes and the ones the agent tool and the run form already take. The
 * conversion to the solver contract's deck units (MPa, t/mm³, 1/K) is
 * `toContractMaterial` below, the one function the backend's expansion step, the
 * agent tool and the run form all call.
 */

export const OPTOMECH_MATERIAL_KINDS = ['glass', 'metal', 'adhesive', 'ceramic', 'polymer', 'composite'] as const;
export const OptomechMaterialKindSchema = z.enum(OPTOMECH_MATERIAL_KINDS);
export type OptomechMaterialKind = z.infer<typeof OptomechMaterialKindSchema>;

export const OPTOMECH_MATERIAL_SCOPES = ['global', 'project'] as const;
export const OptomechMaterialScopeSchema = z.enum(OPTOMECH_MATERIAL_SCOPES);

/** The deck spelling of a material name: it becomes the CalculiX *MATERIAL name. */
export const OPTOMECH_DECK_NAME_RE = /^[A-Za-z0-9_-]{1,80}$/;

/**
 * A name's deck spelling: letters, digits, _ and - kept, everything else dropped
 * (N-BK7 stays; "S-FPL 51" → S-FPL51). The result can still be empty or too long;
 * a caller that writes it tests it against OPTOMECH_DECK_NAME_RE.
 */
export const deckNameOf = (name: string): string => name.replace(/[^A-Za-z0-9_-]/g, '');

/**
 * A number to 12 significant digits. A unit conversion by a power of ten is not
 * always exact (3.86 / 1e9 is 3.8599999999999995e-9, 17 digits), and
 * fea-service's SigFit deck writer refuses a number it cannot write exactly in 16
 * characters (the first assembly run on staging failed at export on exactly that
 * value, 2026-10-07). No datasheet carries 12 digits, so nothing real is lost.
 */
export const sig12 = (v: number): number => Number(v.toPrecision(12));

/** A material in datasheet units, as the agent tool, the run form and a library row's expansion hold it. */
export interface OptomechDatasheetUnits {
  name: string;
  youngs_modulus_gpa: number;
  poisson_ratio: number;
  density_g_cm3: number;
  cte_ppm_per_k: number;
  source: string;
  conductivity_w_mk?: number;
  thermo_optic?: OptomechThermoOptic;
  tensile_strength_mpa?: number;
  strength_source?: string;
}

/**
 * Datasheet units → the solver contract's: E GPa → MPa, ρ g/cm³ → t/mm³, CTE
 * 10⁻⁶/K → 1/K, k W/(m·K) → W/(mm·K); every number to 12 significant digits
 * (sig12), the thermo-optic group as given (it is already in the contract's units).
 */
export function toContractMaterial(m: OptomechDatasheetUnits): OptomechMaterial {
  return {
    name: m.name,
    youngs_modulus_mpa: sig12(m.youngs_modulus_gpa * 1000),
    poisson_ratio: sig12(m.poisson_ratio),
    density_t_per_mm3: sig12(m.density_g_cm3 / 1e9),
    cte_per_k: sig12(m.cte_ppm_per_k / 1e6),
    source: m.source,
    ...(m.conductivity_w_mk === undefined ? {} : { conductivity_w_per_mm_k: sig12(m.conductivity_w_mk / 1000) }),
    ...(m.thermo_optic ? { thermo_optic: m.thermo_optic } : {}),
    ...(m.tensile_strength_mpa === undefined ? {} : { tensile_strength_mpa: sig12(m.tensile_strength_mpa) }),
    ...(m.strength_source === undefined ? {} : { strength_source: m.strength_source }),
  };
}

const entryMeta = {
  /** Where this entry's numbers come from. Required on every entry: a row mixes sources by construction (spec §8.1). */
  source: z.string().min(1),
  /** What the value applies to: a temper, a heat treatment, a wavelength basis. */
  condition: z.string().min(1).optional(),
  /** The source's own stated uncertainty, in percent; recorded when stated, never invented. */
  uncertainty_pct: z.number().nonnegative().optional(),
};

const sortedUniqueTemperatures = (points: { t_c: number }[]) =>
  points.every((p, i) => i === 0 || p.t_c > points[i - 1].t_c);

/**
 * A property against temperature. ONE point is a constant: the datasheet's
 * room-temperature value, which the source states as the value and which a
 * run uses at any reference temperature. TWO OR MORE points are a table:
 * linear interpolation between them, and no extrapolation outside them (the
 * run is refused naming the material, the property and the covered range).
 */
export const OptomechMaterialPointsSchema = z.object({
  points: z.array(z.object({ t_c: z.number(), value: z.number() })).min(1).refine(sortedUniqueTemperatures, { message: 'points must be sorted by t_c, unique in t_c' }),
  ...entryMeta,
});
export type OptomechMaterialPoints = z.infer<typeof OptomechMaterialPointsSchema>;

/**
 * A datasheet's mean expansion coefficient over a stated interval (Schott's
 * "−30/+70 °C", a producer's "20–100 °C"): a secant over THAT interval, usable
 * for a run whose own interval lies inside it (spec §4.1.2).
 */
export const OptomechMaterialCteIntervalsSchema = z.object({
  intervals: z.array(z.object({ t0_c: z.number(), t1_c: z.number(), value: z.number() }).refine((i) => i.t1_c > i.t0_c, { message: 't1_c must exceed t0_c' })).min(1),
  ...entryMeta,
});
export type OptomechMaterialCteIntervals = z.infer<typeof OptomechMaterialCteIntervalsSchema>;

/**
 * Expansion as the strain itself, (L − L_ref)/L_ref against temperature, from
 * a stated reference temperature (NIST publishes this form, relative to 293 K).
 * It gives the exact secant for any interval inside its range, whatever the
 * run's reference temperature. At least two points: one strain is no curve.
 */
export const OptomechMaterialStrainSchema = z.object({
  reference_t_c: z.number(),
  points: z.array(z.object({ t_c: z.number(), value: z.number() })).min(2).refine(sortedUniqueTemperatures, { message: 'points must be sorted by t_c, unique in t_c' }),
  ...entryMeta,
});
export type OptomechMaterialStrain = z.infer<typeof OptomechMaterialStrainSchema>;

/**
 * Glass only: ABSOLUTE dn/dT (1/K) by temperature and wavelength (SigFit SOL
 * DNDT, spec 2026-09-24 §0.4), and the refractive index the same way. A run
 * reads them at ITS wavelength: among the points within OPTOMECH_WAVELENGTH_TOL_UM
 * of it, ONE is the datasheet value and is used as is, recording its own
 * temperature beside the run's reference temperature; none means the row carries
 * no thermo-optic data for that wavelength and the lens is named without it;
 * several are refused by name until an interpolation rule is written (fail
 * closed, rule 2 — the same licence as K, FEA spec §4.8).
 */
export const OptomechMaterialDndtSchema = z.object({
  points: z.array(z.object({ t_c: z.number(), wavelength_um: z.number().positive(), value: z.number() })).min(1),
  ...entryMeta,
});
export type OptomechMaterialDndt = z.infer<typeof OptomechMaterialDndtSchema>;
/** How close a stored wavelength must lie to the run's to be its point: 1 nm, within any catalog's rounding of a spectral line. */
export const OPTOMECH_WAVELENGTH_TOL_UM = 0.001;

/**
 * Glass only: the stress optical coefficient K = K∥ − K⊥ in mm²/N, by temperature
 * and wavelength, as the datasheet prints it (SCHOTT measures K at 589.3 nm and
 * 21 °C, TIE-27 July 2004 §2.2; N-BK7 K = 2.76·10⁻⁶ mm²/N, SCHOTT datasheet
 * collection 2022-02-01 p. 13). What a mechanical case's stress birefringence
 * map is K times (FEA spec 2026-09-24 §4.8, rule 11). ONE point is the constant
 * the datasheet states and a run uses it as is, recording its own wavelength and
 * reference temperature beside it; a row with SEVERAL points is refused by name
 * until an interpolation rule is written in §4.8 (fail closed, rule 2).
 */
export const OptomechMaterialStressOpticalSchema = z.object({
  points: z.array(z.object({ t_c: z.number(), wavelength_um: z.number().positive(), value: z.number() })).min(1),
  ...entryMeta,
});
export type OptomechMaterialStressOptical = z.infer<typeof OptomechMaterialStressOpticalSchema>;

/**
 * The property entries a material row may carry. Every key is optional and an
 * absent property is ABSENT: expansion fails closed naming it (spec rule 1).
 * Strict: an unknown key is a typo the route rejects, not a silent extra.
 */
export const OptomechMaterialPropertiesSchema = z.object({
  youngs_modulus_gpa: OptomechMaterialPointsSchema.optional(),
  poisson_ratio: OptomechMaterialPointsSchema.optional(),
  density_g_cm3: OptomechMaterialPointsSchema.optional(),
  cte_mean_ppm_per_k: OptomechMaterialCteIntervalsSchema.optional(),
  linear_expansion_strain: OptomechMaterialStrainSchema.optional(),
  conductivity_w_mk: OptomechMaterialPointsSchema.optional(),
  specific_heat_j_kgk: OptomechMaterialPointsSchema.optional(),
  dndt_abs_per_k: OptomechMaterialDndtSchema.optional(),
  /** Glass only: n by temperature and wavelength (the same reading rule as dn/dT); with dn/dT it is the thermo-optic pass's input. */
  refractive_index: OptomechMaterialDndtSchema.optional(),
  stress_optical_k_mm2_per_n: OptomechMaterialStressOpticalSchema.optional(),
  /**
   * A tensile strength in MPa with its source: what a stress report's margin is screened against
   * (FEA spec §4.3 stress report, 2026-10-27). ONE point is the figure the source states, and a run
   * uses it as is at any temperature: a metal's handbook design allowable (MIL-HDBK-5J Ftu, with
   * the basis and the product form in `condition`) or a glass datasheet figure. Never estimated;
   * absent when the catalog has none, and then no margin is reported for the body.
   */
  tensile_strength_mpa: OptomechMaterialPointsSchema.refine((e) => e.points.every((p) => p.value > 0), { message: 'a tensile strength is positive' }).optional(),
}).strict();
export type OptomechMaterialProperties = z.infer<typeof OptomechMaterialPropertiesSchema>;

/** One material VERSION as the API serves it (the current one unless a versions listing is asked for). */
export const OptomechMaterialRowSchema = z.object({
  /** The version id: what a run's materials_used names. */
  id: z.string().uuid(),
  /** Stable across versions: what a binding and a run request name. */
  material_id: z.string().uuid(),
  version: z.number().int().positive(),
  scope: OptomechMaterialScopeSchema,
  project_id: z.string().uuid().nullable(),
  kind: OptomechMaterialKindSchema,
  name: z.string(),
  deck_name: z.string().regex(OPTOMECH_DECK_NAME_RE),
  catalog_glass: z.string().nullable(),
  properties: OptomechMaterialPropertiesSchema,
  source: z.string(),
  notes: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  superseded_at: z.string().nullable(),
});
export type OptomechMaterialRow = z.infer<typeof OptomechMaterialRowSchema>;

export const OptomechMaterialListSchema = z.object({ materials: z.array(OptomechMaterialRowSchema) });
export const OptomechMaterialVersionListSchema = z.object({ versions: z.array(OptomechMaterialRowSchema) });

/**
 * Body of POST /optomech/materials (a new project-scoped material) and of
 * POST /optomech/materials/{materialId}/versions (a new version of one the
 * project owns). Global rows are written from the admin area (P2), never here.
 */
export const OptomechMaterialCreateSchema = z.object({
  project_id: z.string().uuid(),
  kind: OptomechMaterialKindSchema,
  name: z.string().min(1).max(120),
  deck_name: z.string().regex(OPTOMECH_DECK_NAME_RE),
  catalog_glass: z.string().min(1).optional(),
  properties: OptomechMaterialPropertiesSchema,
  source: z.string().min(1),
  notes: z.string().min(1).optional(),
});
export type OptomechMaterialCreate = z.infer<typeof OptomechMaterialCreateSchema>;

/**
 * The four datasheet numbers with what makes them a sourced row (spec §6.3):
 * the temperature E, ν and ρ are quoted at, the interval the mean CTE is
 * stated over, and the source. What a user's answer — in chat or in the run
 * form — is kept as, through `propertiesFromDatasheet`.
 */
export const OptomechDatasheetMaterialSchema = z.object({
  kind: OptomechMaterialKindSchema,
  name: z.string().min(1).max(120),
  youngs_modulus_gpa: z.number().positive(),
  poisson_ratio: z.number().min(0).lt(0.5),
  density_g_cm3: z.number().positive(),
  /** The datasheet's MEAN coefficient over `cte_interval_c`, 10⁻⁶/K. */
  cte_ppm_per_k: z.number(),
  cte_interval_c: z.tuple([z.number(), z.number()]).refine(([a, b]) => b > a, { message: 'the CTE interval must run from a lower to a higher temperature' }),
  /** The temperature E, ν and ρ are quoted at, °C. */
  quoted_at_c: z.number(),
  source: z.string().min(1),
  condition: z.string().min(1).optional(),
  catalog_glass: z.string().min(1).optional(),
  /**
   * Glass only: the datasheet's stress optical coefficient K, kept as ONE point of
   * `properties.stress_optical_k_mm2_per_n` with its own source (the datasheet,
   * and the wavelength and temperature it states — SCHOTT: 589.3 nm, 21 °C,
   * TIE-27 §2.2). Omitted, the row carries no K and a mechanical run reports no
   * birefringence map for the body, naming it (FEA spec §4.8).
   */
  stress_optical: z.object({
    k_mm2_per_n: z.number(),
    wavelength_um: z.number().positive(),
    t_c: z.number(),
    source: z.string().min(1),
  }).optional(),
  /**
   * Glass only: the datasheet's index and ABSOLUTE dn/dT at one wavelength and
   * temperature, kept as one point each of `properties.refractive_index` and
   * `properties.dndt_abs_per_k` with their own source (SCHOTT publishes dn/dT_abs
   * per wavelength and 20 °C interval). Omitted, the row carries no thermo-optic
   * data and a thermal run reports no OPD map for the body, naming it.
   */
  thermo_optic: z.object({
    index: z.number().gt(1),
    dndt_abs_per_k: z.number(),
    wavelength_um: z.number().positive(),
    t_c: z.number(),
    source: z.string().min(1),
  }).optional(),
  /** The datasheet's thermal conductivity in W/(m·K) at `quoted_at_c`, kept as one point of `properties.conductivity_w_mk`; what a gradient over several bodies needs on each. */
  conductivity_w_mk: z.number().positive().optional(),
  /**
   * The source's tensile strength in MPa, kept as one point of `properties.tensile_strength_mpa` with
   * its own source and condition (the basis, the temper, the product form): what a stress report's
   * margin is screened against. Omitted, the row carries no strength and a run reports no margin for
   * the body. Never estimated.
   */
  tensile_strength: z.object({
    mpa: z.number().positive(),
    source: z.string().min(1),
    condition: z.string().min(1).optional(),
  }).optional(),
});
export type OptomechDatasheetMaterial = z.infer<typeof OptomechDatasheetMaterialSchema>;

/**
 * Who or what wrote a binding. Resolution writes 'library_match' only; 'user',
 * 'agent' and 'step_declared_confirmed' are human-confirmed and sticky (spec
 * 2026-10-16 §4.3). 'catalog' is written by the composition build for a
 * catalog part's own body (spec 2026-10-19 §7): human-free and NOT sticky,
 * since the catalog is the authority for its own parts and a person may rebind.
 */
export const OPTOMECH_BINDING_HOW = ['library_match', 'user', 'agent', 'step_declared_confirmed', 'catalog'] as const;
export const OptomechBindingHowSchema = z.enum(OPTOMECH_BINDING_HOW);
export type OptomechBindingHow = z.infer<typeof OptomechBindingHowSchema>;

/** The evidence behind a binding (spec §5.3). */
export const OPTOMECH_BINDING_CONFIDENCE = ['exact', 'fingerprint', 'name', 'declared'] as const;
export const OptomechBindingConfidenceSchema = z.enum(OPTOMECH_BINDING_CONFIDENCE);

export const OptomechBodyBindingSchema = z.object({
  retained_geometry_ref: z.string(),
  occurrence_path: z.string(),
  material_id: z.string().uuid(),
  how: OptomechBindingHowSchema,
  confidence: OptomechBindingConfidenceSchema,
  evidence: z.record(z.string(), z.unknown()),
  bound_by: z.string(),
  bound_at: z.string(),
});
export type OptomechBodyBinding = z.infer<typeof OptomechBodyBindingSchema>;

/**
 * Body of PUT /optomech/bodies/binding: the user's answer for one body of the
 * conversation's retained file — an existing material by its stable id, or the
 * datasheet numbers to keep as a new project material and bind in one step.
 */
export const OptomechBodyBindingUpsertSchema = z.union([
  z.object({
    conversation_id: z.string().uuid(),
    occurrence_path: z.string().min(1),
    /** The stable id of a material the conversation's project can read (global, or its own). */
    material_id: z.string().uuid(),
  }),
  z.object({
    conversation_id: z.string().uuid(),
    occurrence_path: z.string().min(1),
    material: OptomechDatasheetMaterialSchema,
  }),
]);
export type OptomechBodyBindingUpsert = z.infer<typeof OptomechBodyBindingUpsertSchema>;

/**
 * GET /optomech/bodies — every meshable body of the conversation's retained
 * CAD file (the import's elements as lenses, its `mount` solids), joined with
 * its binding and the binding's current material (spec §4.4: a join at read
 * time, never a field on the system JSON). `retained_geometry_ref` is null and
 * `bodies` empty for a conversation without a retained import.
 */
export const OptomechBodySchema = z.object({
  occurrence_path: z.string(),
  name: z.string(),
  kind: z.enum(['lens', 'mount']),
  /** The system surfaces a lens owns (empty for a mount). */
  surfaces: z.array(z.number().int()),
  /**
   * The element a lens body belongs to: a cemented element's bodies (one per glass) share it, and each is bound
   * to its own material. The import's element id for an imported lens; null for a mount.
   */
  element_id: z.string().nullable(),
  /** The lens's glass on its first surface, when the system names one. */
  glass: z.string().nullable(),
  /**
   * The STEP file's own material attribute on this body (spec 2026-10-16 §5.4): a HINT the agent
   * offers for confirmation, never a property set. Null when the file declares none (the ordinary
   * case) and on imports saved before the importer reported it.
   */
  declared_material: CadDeclaredMaterialSchema.nullable(),
  binding: z.object({
    how: OptomechBindingHowSchema,
    confidence: OptomechBindingConfidenceSchema,
    bound_by: z.string(),
    bound_at: z.string(),
    /** The bound material's CURRENT version, or null when the project can no longer read it. */
    material: OptomechMaterialRowSchema.nullable(),
  }).nullable(),
});
export type OptomechBody = z.infer<typeof OptomechBodySchema>;

export const OptomechBodyListSchema = z.object({
  retained_geometry_ref: z.string().nullable(),
  bodies: z.array(OptomechBodySchema),
});
export type OptomechBodyList = z.infer<typeof OptomechBodyListSchema>;

/**
 * Per body, what a run expanded (optomech_runs.materials_used, spec §4.5): the
 * material VERSION with the interval its CTE was the secant over, or the inline
 * numbers' source for a body the request carried numbers for.
 */
export const OptomechMaterialUsedSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('library'),
    /** The single-element run's lens, or the assembly body. */
    occurrence_path: z.string().nullable(),
    material_version_id: z.string().uuid(),
    material_id: z.string().uuid(),
    version: z.number().int(),
    name: z.string(),
    /** [t0, t1] the CTE was taken over; equal for the degenerate interval of a case with no temperature change. */
    cte_interval_c: z.tuple([z.number(), z.number()]),
    /** The composed per-property source string that went to fea-service as material.source. */
    source: z.string(),
    /**
     * The stress optical coefficient forwarded as the fea-service material's
     * `stress_optical_k_mm2_per_n` (FEA spec §4.8): the datasheet point used as
     * is, and the run's wavelength and reference temperature it was used at.
     * Absent when the row carries no K.
     */
    stress_optical_k: z.object({
      k_mm2_per_n: z.number(),
      at_wavelength_um: z.number(),
      at_t_c: z.number(),
      source: z.string(),
      run_wavelength_um: z.number(),
      run_reference_temperature_c: z.number(),
    }).optional(),
    /** The index and dn/dT forwarded as the fea-service material's `thermo_optic`: the row's points at the run's wavelength, used as is. Absent when the row carries none there. */
    thermo_optic: z.object({
      index: z.number(),
      index_at_c: z.number(),
      dndt_per_k: z.number(),
      dndt_at_c: z.number(),
      at_wavelength_um: z.number(),
      source: z.string(),
      run_wavelength_um: z.number(),
    }).optional(),
    /** The conductivity forwarded, W/(mm·K), read at the reference temperature. Absent when the row carries none. */
    conductivity_w_per_mm_k: z.number().optional(),
    /** The tensile strength forwarded as the fea-service material's `tensile_strength_mpa` and `strength_source`. Absent when the row carries none. */
    tensile_strength: z.object({ mpa: z.number(), source: z.string() }).optional(),
  }),
  z.object({
    kind: z.literal('inline'),
    occurrence_path: z.string().nullable(),
    name: z.string(),
    source: z.string(),
  }),
]);
export type OptomechMaterialUsed = z.infer<typeof OptomechMaterialUsedSchema>;
